import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { authAPI } from '../services/api';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await authAPI.login(username, password);

      // API Response structure:
      // {
      //   "success": true,
      //   "data": {
      //     "Token": "c68f325246ec63e4578938270a12aa5b",      // Legacy token
      //     "token": "eyJhbGciOiJIUzI1NiIs...",              // JWT token (use this)
      //     "data": { id, username, role, location_id, company_id, ... },  // User data
      //     "company_id": "78"
      //   }
      // }

      console.log('🔐 Full auth response received:', response);

      if (response && response.data) {
        // Use JWT token (lowercase 'token') for API authentication
        // Fallback to legacy Token if JWT not available
        const jwtToken = response.data.token;        // Token.
        const legacyToken = response.data.Token;   // Legacy token
        const authToken = jwtToken || legacyToken;

        console.log('🔑 JWT Token:', jwtToken ? 'Found' : 'Not found');
        console.log('🔑 Legacy Token:', legacyToken ? 'Found' : 'Not found');
        console.log('🔑 Using token:', authToken ? 'Token available' : 'No token');

        // Get user data from nested data.data object
        const userData = response.data.data;
        const companyId = response.data.company_id;

        // Choose storage based on remember me
        const storage = rememberMe ? localStorage : sessionStorage;

        // Store JWT token for API authentication
        if (authToken) {
          storage.setItem('authToken', authToken);
          storage.setItem('legacyToken', legacyToken);
          storage.setItem('username', username);
          console.log('💾 Token stored in', rememberMe ? 'localStorage' : 'sessionStorage');
        } else {
          console.warn('⚠️ No token found in login response');
        }

        // Store user data
        if (userData) {
          storage.setItem('userData', JSON.stringify(userData));
          console.log('👤 User data stored:', {
            id: userData.id,
            username: userData.username,
            role: userData.role,
            status: userData.status
          });

          // Store location IDs for filtering
          if (userData.location_id && Array.isArray(userData.location_id)) {
            storage.setItem('locationIds', JSON.stringify(userData.location_id));
            console.log('📍 Location IDs stored:', userData.location_id);
          }

          // Store company info
          if (userData.company_id && Array.isArray(userData.company_id)) {
            storage.setItem('userCompanies', JSON.stringify(userData.company_id));
            console.log('🏢 User companies stored:', userData.company_id);
          }
        }

        // Store company ID
        if (companyId) {
          storage.setItem('companyId', companyId);
          console.log('🏢 Company ID stored:', companyId);
        }

        console.log('✅ Login successful, navigating to dashboard');
        navigate('/dashboard', { replace: true });
      } else {
        throw new Error('Invalid response from login API');
      }
    } catch (err) {
      console.error('❌ Login error:', err);
      setError(err.message || 'Login failed. Please check your credentials.');
      setLoading(false);
    }
  };

  return (
    <div className="h-screen overflow-y-auto flex items-start justify-center pt-20" style={{ backgroundColor: '#f6f6fe' }}>
      <div className="w-full max-w-7xl mx-auto px-4 py-8 flex items-start justify-center min-h-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 w-full items-center max-w-6xl">
          {/* Left Side - Branding and Illustration */}
          <div className="flex flex-col items-center lg:items-start justify-start space-y-6 lg:space-y-8 px-4 lg:px-0 lg:-ml-10 mb-8 lg:mb-0">
            {/* Logo and Brand Name */}
            <div className="flex items-center space-x-4">
              <div className="w-[60px] h-[64px] lg:w-[70px] lg:h-[74px] mt-2 lg:mt-5 rounded-full bg-yellow-50 flex items-center justify-center overflow-hidden border border-gray-100">
                <img
                  src="/assests/logo_image-2.png"
                  alt="MOSY Logo"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex flex-col">
                <h1 className="text-[28px] lg:text-[33px] mt-2 lg:mt-4 lg:-mb-2 font-roboto font-bold text-[#004e92] flex-shrink-0 leading-none -tracking-[0.07em]">MOSY</h1>
              </div>
            </div>

            {/* Construction Vehicles Illustration */}
            <div className="w-full flex justify-center lg:justify-start relative mt-2 lg:mt-4">
              <img
                src="/assests/mosy.inloginimg.png"
                alt="Construction Vehicles"
                className="w-full h-auto max-w-[260px] sm:max-w-[300px] lg:max-w-[390px] -lg:translate-x-[4px] lg:-translate-y-[10px]"
                style={{
                  objectFit: 'contain',
                  mixBlendMode: 'multiply',
                  filter: 'contrast(1.1) brightness(1.05)',
                  backgroundColor: 'transparent'
                }}
              />
            </div>
          </div>

          {/* Right Side - Login Form */}
          <div className="w-full flex items-center justify-center lg:justify-end">
            <div className="w-full max-w-[470px] h-[323px] bg-white rounded-2xl border border-gray-400
                shadow-[0px_0px_0px_rgba(0,0,0,0.12)]
                p-6 translate-y-[30px]">
              <h2 className="text-[15px] font-semibold text-neutral-950 mb-3 translate-y-[2px] font-sans">Sign in</h2>

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Error Message */}
                {error && (
                  <div className="p-2.5 bg-red-50 border border-red-100 rounded-md">
                    <p className="text-xs text-red-600">{error}</p>
                  </div>
                )}

                {/* Username Field */}
                <div className="space-y-2">
                  <Label htmlFor="username" className="text-[15px] font-medium font-sans  text-neutral-800 tracking-wide">
                    Username
                  </Label>
                  <Input
                    id="username"
                    type="text"
                    placeholder="Username"
                    value={username}
                    onChange={(e) => {
                      setUsername(e.target.value);
                      setError('');
                    }}
                    required
                    disabled={loading}
                    className="w-full h-8 text-sm pl-3 rounded-base bg-white transition-all placeholder:text-xs placeholder:text-gray-500 border border-gray-300 focus:border-blue-400 focus:ring-0 focus-visible:border-blue-400 focus-visible:ring-2 focus-visible:ring-blue-100 outline-none focus:outline-none shadow-none"
                  />
                </div>

                {/* Password Field */}
                <div className="space-y-2 -translate-y-[4px]">
                  <Label htmlFor="password" className="text-[15px] font-medium font-sans text-neutral-800 tracking-wide">
                    Password
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setError('');
                    }}
                    required
                    disabled={loading}
                    className="w-full h-8  text-sm rounded-base bg-white transition-all placeholder:text-xs placeholder:text-gray-500 border border-gray-300 focus:border-blue-400 focus:ring-0 focus-visible:border-blue-400 focus-visible:ring-2 focus-visible:ring-blue-100 outline-none focus:outline-none shadow-none" />
                </div>

                {/* Remember Me Checkbox */}
                <div className="flex items-center space-x-2 -space-y-6 py-1">
                  <Checkbox
                    id="remember"
                    className="w-3 h-3 -translate-y-[12px] rounded border-gray-300 data-[state=checked]:bg-[#004e92] data-[state=checked]:border-[#004e92] focus:border-blue-400 focus:ring-[3px] focus:ring-blue-100 focus-visible:border-blue-400 focus-visible:ring-[3px] focus-visible:ring-blue-100 outline-none focus:outline-none focus:shadow-none transition-all"
                    checked={rememberMe}
                    onCheckedChange={(checked) => setRememberMe(checked)}
                    disabled={loading}
                  />
                  <Label
                    htmlFor="remember"
                    className="text-[10px] font-medium font-sans text-neutral-950 tracking-wide cursor-pointer"
                  >
                    Remember me
                  </Label>
                </div>

                {/* Login Button */}
                <Button
                  type="submit"
                  className="w-[6.1rem] h-6.4 px-[2.5rem] bg-[#004e92] translate-y-[-4px] hover:bg-[#003d73] text-white font-bold text-xs rounded shadow-sm transition-colors disabled:opacity-50"
                >
                  {loading ? '...' : 'Login'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;

